import Spinner from '@/components/atoms/Spinner'
import { useAppDispatch, useAppSelector } from '@/store/hooks'
import Container from '@/components/atoms/Container'
import Button from '@/components/atoms/Button'
import Icon from '@/components/atoms/Icon'
import { twMerge } from 'tailwind-merge'
import classNames from 'classnames'
import { contentCreators } from '@/store/ducks/content'
import { useEffect, useRef, useState } from 'react'

import './style.css'
import './prism.css'
import libraryService from '@/services/libraryService'
import { useNavigate } from 'react-router-dom'

export function LibraryList() {
	const myRef = useRef(null)
	const dispatch = useAppDispatch()

	const [moduleId, setModuleId] = useState(0)

	const {
		auth: { user },
		content: { moduleOpened },
		matriculate: { classSelect },
	} = useAppSelector((state) => state)

	const navigate = useNavigate()

	const { data: proficiencyData, isSuccess: isSuccessProficiency } =
		libraryService.getProficiency()

	const {
		execute: getUnits,
		data: unitsData,
		isLoading: isLoadingUnits,
		isSuccess: isSuccessUnits,
	} = libraryService.getUnitsMutation()

	const {
		execute: getLesson,
		data: lessonData,
		isLoading: isLoadingLesson,
	} = libraryService.getLessonMutation()

	const {
		execute: completeTopic,
		isLoading: isLoadingCompleteTopic,
		// isSuccess,
	} = libraryService.completeTopic()

	const { data: progressData, isLoading: isLoadingProgress } =
		libraryService.getProgress()

	const findOnboarding = user?.onboarding_all?.find(
		(onboading) => onboading.curso_id === classSelect?.curso_id
	)

	const getDefaultModuleId = (cursoId?: number) => {
		switch (cursoId) {
			case 2:
				return 20
			case 1:
				return 1
			default:
				return 1
		}
	}

	useEffect(() => {
		if (isSuccessProficiency && proficiencyData) {
			if (!moduleId) setModuleId(getDefaultModuleId(findOnboarding?.curso_id))

			if (!classSelect?.curso_id) {
				if (user?.modo_curso_unico) navigate('/curso')

				navigate('/')
			}

			const courseId = Number(classSelect?.curso_id)

			if (moduleId) {
				getUnits({ courseId, moduleId })
			}

			scrollToTop()
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isSuccessProficiency, moduleId])

	function loadFirstIncompleteTopic() {
		if (!unitsData) return
		for (const unit of unitsData) {
			const firstIncompleteTopic = unit.topicos.find(
				(topic) => !progressData?.concluidos?.includes(topic.id)
			)

			if (firstIncompleteTopic) {
				getLesson({ topicId: firstIncompleteTopic.id })
				handleUnit(unit.id)
				break
			}
		}
	}

	useEffect(() => {
		if (isSuccessUnits && unitsData) {
			loadFirstIncompleteTopic()
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isSuccessUnits, unitsData])

	function handleTopic(topicId: number) {
		if (moduleOpened?.length > 1) dispatch(contentCreators.reset())

		getLesson({ topicId })
	}

	function handleUnit(idx: number) {
		dispatch(contentCreators.setOrRemove({ idx }))
	}

	useEffect(() => {
		if ((!isLoadingUnits || !isLoadingLesson) && lessonData) {
			if (!moduleOpened?.includes(lessonData.unidade_id)) {
				dispatch(contentCreators.setOrRemove({ idx: lessonData.unidade_id }))
			}
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [lessonData])

	const handleCompleteTopic = ({ lessonId }: { lessonId: number }) => {
		completeTopic({
			topicoId: lessonId,
		})
	}

	const scrollToTop = () => {
		window.scrollTo({
			top: 0,
			behavior: 'smooth',
		})
	}

	return (
		<div className='w-full flex flex-col pt-10'>
			{proficiencyData && (
				<div className='flex justify-start md:justify-center items-center bg-gradient-to-r from-zinc-950 to-zinc-900 mb-5 py-2 rounded'>
					<div className='overflow-x-auto md:overflow-visible'>
						<div className='flex justify-start md:justify-center items-center gap-6 mr-2'>
							{proficiencyData
								?.filter((item) => item.modulo_id)
								?.map((module, key) => (
									<button
										key={key}
										onClick={() => setModuleId(module.modulo_id || 0)}
										className={`relative my-2 text-sm h-[40px] rounded min-w-max px-4 capitalize border border-dotted transition-all duration-300 border-light-pink text-white ${
											Number(moduleId) === Number(module.modulo_id)
												? 'shadow-[0px_0px_18.4px_0px_rgba(255,55,125,0.5)] bg-light-pink'
												: 'bg-zinc-900'
										}`}
									>
										{module.nivel}
										{key > 0 && (
											<span className='absolute left-[-25px] top-1/2 w-[25px] h-[2px] bg-light-pink z-0 rounded'></span>
										)}
									</button>
								))}
						</div>
					</div>
				</div>
			)}
			<Container className='w-full px-0 !max-w-[1420px] xl:px-8 2xl:px-0'>
				<div className='relative w-full py-6'>
					{(() => {
						if (isLoadingProgress || isLoadingUnits) {
							return (
								<div className='flex justify-center'>
									<Spinner
										className={
											'inline w-10 h-10 text-gray-200 animate-spin fill-brand-primary'
										}
									/>
								</div>
							)
						}

						const arrayOfCompletedUnitIds = unitsData
							?.map((unit: { topicos: any[]; id: any }) => {
								// Verifica se a unidade tem tópicos. Se não tiver, não considera como completa.
								if (unit.topicos.length === 0) {
									return null
								}

								// Verifica se todos os tópicos da unidade estão completos
								const allTopicsCompleted = unit.topicos.every(
									(topic) =>
										topic?.id && progressData?.concluidos?.includes(topic.id)
								)

								return allTopicsCompleted ? unit.id : null
							})
							.filter((unitId) => unitId !== null)

						return (
							<Container className='max-w-full px-0 sm:px-0 md:px-0 2lg:px-0 xl:px-0'>
								<div
									className={twMerge(
										classNames(
											'flex flex-col w-full 2lg:grid 2lg:grid-cols-[minmax(337px,_437px)_1fr] justify-between gap-8',
											{ '!hidden': !unitsData?.length }
										)
									)}
								>
									<div className='flex-col w-full'>
										<div className='flex flex-col gap-3'>
											<div
												className='flex flex-col w-full bg-zinc-900 rounded-lg py-6 px-3 gap-3 overflow-auto'
												id='sidebar-content'
											>
												{(() => {
													if (isLoadingUnits || !unitsData) {
														return (
															<div className='flex justify-center'>
																<Spinner
																	className={
																		'inline w-10 h-10 text-gray-200 animate-spin fill-brand-primary'
																	}
																/>
															</div>
														)
													}

													return (
														<div className='flex flex-col gap-8 transition-all duration-1000'>
															<div className='flex flex-col gap-0'>
																{unitsData?.map((unit, key) => {
																	const isOpen = moduleOpened.includes(unit.id)
																	const isCompleted =
																		arrayOfCompletedUnitIds?.length &&
																		arrayOfCompletedUnitIds?.includes(unit.id)

																	return (
																		<div
																			className={classNames(
																				'flex flex-col px-4 rounded-lg gap-6 last-of-type:mb-0 last-of-type:gap-0 transition-all duration-500',
																				{
																					'bg-zinc-800 py-4 mb-6 last-of-type:gap-6':
																						isOpen,
																				}
																			)}
																			key={key}
																			id={`unit-${unit.id}`}
																		>
																			<div className='flex flex-col gap-2'>
																				<div
																					className='flex group justify-between items-center cursor-pointer gap-1'
																					onClick={() => handleUnit(unit.id)}
																				>
																					<div
																						key={key}
																						className='flex items-center gap-2'
																					>
																						<p
																							className={
																								'text-lg leading-[140%] text-zinc-50 font-bold'
																							}
																						>
																							{unit.nome}
																						</p>
																					</div>

																					{(() => {
																						if (isCompleted) {
																							return (
																								<div>
																									<Icon
																										name='check-content'
																										width='24'
																										height='24'
																										className='w-5 h-5'
																									/>
																								</div>
																							)
																						}

																						return (
																							<div>
																								<Icon
																									name='arrow-up'
																									width='22'
																									height='22'
																									className={twMerge(
																										'text-[#071830] dark:text-[#F7F9FB] transition-transform duration-500',
																										classNames({
																											'-rotate-180': isOpen,
																										})
																									)}
																								/>
																							</div>
																						)
																					})()}
																				</div>
																			</div>
																			<div
																				className={twMerge(
																					'flex flex-col h-full transition-all duration-500 overflow-clip',
																					classNames({
																						'max-h-max': moduleOpened.includes(
																							unit?.id
																						),
																						'max-h-0': !moduleOpened.includes(
																							unit?.id
																						),
																					})
																				)}
																			>
																				<div className='flex flex-col gap-3'>
																					{unit.topicos.map(
																						(topicMap, topicIdx) => {
																							const isCompleted =
																								progressData?.concluidos?.includes(
																									topicMap?.id
																								)

																							return (
																								<div
																									className='group'
																									key={topicIdx}
																									id={`topic-${topicMap.id}`}
																									onClick={() =>
																										handleTopic(topicMap.id)
																									}
																								>
																									<div
																										className={classNames(
																											'flex max-w-max py-1 rounded-md cursor-pointer transition-all duration-200',
																											{
																												'text-opacity-0':
																													lessonData?.id ===
																													topicMap.id,
																											}
																										)}
																									>
																										<div className='flex items-center gap-4'>
																											<span
																												className={classNames(
																													'flex min-w-[20px] min-h-[20px] md:min-w-[20px] md:min-h-[20px] justify-center items-center border border-[#829ABA] rounded-full',
																													{
																														'border-none':
																															isCompleted,
																													}
																												)}
																											>
																												{isCompleted && (
																													<Icon
																														name='check-content'
																														width='20'
																														height='20'
																													/>
																												)}
																											</span>
																											<p
																												className={twMerge(
																													classNames(
																														'flex items-center text-base font-medium text-zinc-50 group-hover:opacity-100 opacity-70 leading-[155%] gap-2',
																														{
																															'opacity-100 font-bold':
																																lessonData?.id ===
																																topicMap.id,
																														}
																													)
																												)}
																											>
																												{topicMap.nome}
																											</p>
																										</div>
																									</div>
																								</div>
																							)
																						}
																					)}
																				</div>
																			</div>
																		</div>
																	)
																})}
															</div>
														</div>
													)
												})()}
											</div>
										</div>
									</div>
									{(() => {
										if (isLoadingLesson) {
											return (
												<div className='flex justify-center'>
													<Spinner
														className={
															'inline w-10 h-10 text-gray-200 animate-spin fill-brand-primary'
														}
													/>
												</div>
											)
										}

										if (lessonData) {
											const isCompleted = progressData?.concluidos?.includes(
												lessonData?.id
											)

											return (
												<div className='flex flex-col w-full'>
													<div className='flex flex-col gap-7'>
														<h1 className='text-3xl text-white leading-[120%] font-bold'>
															{lessonData.nome}
														</h1>
														<div className='flex flex-col gap-10'>
															<div id='prism-style'>
																<div
																	dangerouslySetInnerHTML={{
																		__html: lessonData.conteudo,
																	}}
																	ref={myRef}
																	id='content-style'
																/>
															</div>
														</div>
													</div>

													<div className='flex justify-end items-end'>
														<Button
															className={twMerge(
																'w-full max-w-[270px] h-[50px] rounded-full transition-all duration-700',
																classNames({
																	'bg-[#15ca71] dark:bg-[#15ca71] text-white dark:text-white border-none':
																		isLoadingCompleteTopic || isCompleted,
																})
															)}
															onClick={() =>
																handleCompleteTopic({
																	lessonId: lessonData.id,
																})
															}
															isLoading={isLoadingCompleteTopic}
														>
															<Icon
																name='check-circle-outline'
																width='18'
																height='18'
																className={twMerge(
																	'stroke-brand-text-primary',
																	classNames({
																		'dark:stroke-white': isCompleted,
																	})
																)}
															/>
															<p className='ml-4'>
																{isCompleted
																	? 'Tópico Completo'
																	: 'Marcar como concluído'}
															</p>
														</Button>
													</div>
												</div>
											)
										}

										return <></>
									})()}
								</div>
							</Container>
						)
					})()}
				</div>
			</Container>
		</div>
	)
}
